import {useContext, useEffect, useState} from 'react';
import {Link, useLocation} from 'react-router-dom';
import Section from './UI/Section';
import LabelAndInput from './UI/LabelAndInput';
import {Context} from '../context';
import Button from './UI/Button';
import ErrorNote from './UI/ErrorNote';

const RESEND_COOLDOWN_SECONDS = 60;

function RegisterVerification() {
  const {localeText, resendVerificationEmail, setSnackBarMessage, loading} =
    useContext(Context);
  const location = useLocation();
  const [email, setEmail] = useState(location.state?.email || '');
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  const [error, setError] = useState('');

  useEffect(() => {
    if (location.state?.verificationEmailSent) {
      setCooldownSeconds(RESEND_COOLDOWN_SECONDS);
    }
  }, [location.state?.verificationEmailSent]);

  useEffect(() => {
    if (cooldownSeconds <= 0) {
      return undefined;
    }

    const timer = setInterval(() => {
      setCooldownSeconds(seconds => (seconds <= 1 ? 0 : seconds - 1));
    }, 1000);

    return () => clearInterval(timer);
  }, [cooldownSeconds]);

  const submitHandler = e => {
    e.preventDefault();
    setError('');

    if (!email) {
      setError(
        localeText('rw_register_verification_email_required', 'Email is required.')
      );
      return;
    }

    if (cooldownSeconds > 0) {
      return;
    }

    resendVerificationEmail(email).then(ret => {
      const throttleError = ret?.errors?.find(
        err => err.code === 'verification-email-resend-throttled'
      );

      if (throttleError) {
        setCooldownSeconds(RESEND_COOLDOWN_SECONDS);
        setError(
          localeText(
            'rw_register_verification_resend_throttled',
            'A verification email was sent recently. Please wait before requesting another.'
          )
        );
        return;
      }

      if (ret?.data?.resendVerificationEmail?.success && !ret?.errors) {
        setCooldownSeconds(RESEND_COOLDOWN_SECONDS);
        setSnackBarMessage(
          localeText(
            'rw_register_verification_resent',
            'Verification email sent. Please check your inbox.'
          )
        );
        return;
      }

      setError(
        localeText(
          'rw_register_verification_resend_error',
          'Unable to resend verification email. Please try again later.'
        )
      );
    });
  };

  const resendDisabled = loading || cooldownSeconds > 0;
  const resendButtonLabel =
    cooldownSeconds > 0
      ? localeText(
          'rw_register_verification_resend_cooldown',
          `Resend available in ${cooldownSeconds}s`
        ).replace('{seconds}', cooldownSeconds)
      : localeText(
          'rw_register_verification_resend_button',
          'Resend Verification Email'
        );

  return (
    <Section
      sectionId='register-verification'
      heading={localeText('rw_register_verification_header', 'Verify Your Email')}
      className='h-full'
    >
      <div className='max-w-xl flex flex-col gap-6'>
        <p>
          {localeText(
            'rw_register_msg_verify_email',
            'Account created! Please check your email to verify your account before logging in.'
          )}
        </p>

        <form action='' className='w-full max-w-md' onSubmit={submitHandler}>
          <p>
            {localeText(
              'rw_register_verification_resend_prompt',
              "Didn't receive an email? Enter your address below to resend the verification link."
            )}
          </p>
          <LabelAndInput
            name='email'
            label={localeText('rw_title_email', 'Email')}
            type='email'
            placeholder={localeText('rw_title_email_placeholder', 'Email')}
            className='w-full'
            value={email}
            onChange={e => setEmail(e.target.value)}
            required
          />
          {error && <ErrorNote>{error}</ErrorNote>}
          <Button
            className='w-full mt-6 bg-red hover:text-white'
            type='submit'
            loading={loading}
            disabled={resendDisabled}
            loadingtext={localeText(
              'rw_register_verification_resending',
              'Sending...'
            )}
          >
            {resendButtonLabel}
          </Button>
        </form>

        <Link to='/login' className='text-red underline hover:text-dark w-fit'>
          {localeText('rw_title_sign_in', 'Sign In')}
        </Link>
      </div>
    </Section>
  );
}

export default RegisterVerification;
